import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Inbox, Search } from 'lucide-react';
import './EmptyState.css';

/**
 * Иллюстрация: стопка карточек-заглушек, на переднем плане — значок раздела.
 * В режиме поиска поверх значка — лупа.
 */
const EmptyIllustration = ({ icon: Icon, search }) => (
  <div className="empty-state__art" aria-hidden="true">
    <svg className="empty-state__svg" viewBox="0 0 200 140" fill="none">
      <circle cx="100" cy="72" r="58" className="empty-state__halo" />

      <rect x="46" y="30" width="96" height="62" rx="10" className="empty-state__card empty-state__card--back" transform="rotate(-8 94 61)" />
      <rect x="58" y="34" width="96" height="62" rx="10" className="empty-state__card empty-state__card--front" transform="rotate(5 106 65)" />
      <rect x="72" y="50" width="44" height="6" rx="3" className="empty-state__line" transform="rotate(5 94 53)" />
      <rect x="71" y="62" width="64" height="6" rx="3" className="empty-state__line empty-state__line--soft" transform="rotate(5 103 65)" />
      <rect x="70" y="74" width="54" height="6" rx="3" className="empty-state__line empty-state__line--soft" transform="rotate(5 97 77)" />

      <circle cx="34" cy="38" r="3" className="empty-state__dot" />
      <circle cx="170" cy="28" r="4" className="empty-state__dot empty-state__dot--accent" />
      <circle cx="176" cy="104" r="2.5" className="empty-state__dot" />
      <path d="M28 98l3 6 6 3-6 3-3 6-3-6-6-3 6-3z" className="empty-state__spark" />
    </svg>

    <span className="empty-state__badge">
      <Icon size={30} strokeWidth={1.75} />
      {search && (
        <span className="empty-state__badge-search">
          <Search size={14} strokeWidth={2.5} />
        </span>
      )}
    </span>
  </div>
);

/**
 * Пустое состояние списка/блока.
 *
 * @param {import('lucide-react').LucideIcon} [icon] — значок раздела на иллюстрации
 * @param {'empty'|'search'} [variant] — «пока ничего нет» или «ничего не найдено по запросу/фильтру»
 * @param {string} title
 * @param {React.ReactNode} [description] — вторичный текст
 * @param {{label: string, to?: string, onClick?: Function, icon?: import('lucide-react').LucideIcon}} [action]
 * @param {boolean} [compact] — уменьшенный вариант для блоков внутри форм
 */
export const EmptyState = ({
  icon = Inbox,
  variant = 'empty',
  title,
  description,
  action,
  compact = false,
  className = '',
  children,
}) => {
  const ActionIcon = action?.icon;
  const actionContent = action && (
    <>
      {ActionIcon && <ActionIcon size={16} className="me-2" />}
      {action.label}
    </>
  );

  return (
    <div className={`empty-state ${compact ? 'empty-state--compact' : ''} ${className}`}>
      <EmptyIllustration icon={icon} search={variant === 'search'} />
      {title && <h6 className="empty-state__title">{title}</h6>}
      {description && <p className="empty-state__text">{description}</p>}
      {action && (
        <div className="empty-state__actions">
          {action.to ? (
            <Button as={Link} to={action.to} variant="dark" size="sm">
              {actionContent}
            </Button>
          ) : (
            <Button variant="dark" size="sm" onClick={action.onClick}>
              {actionContent}
            </Button>
          )}
        </div>
      )}
      {children}
    </div>
  );
};
